import type { DataOption } from '@/common.types'
import type { TFunction } from 'i18next'
import type { FieldOption } from '../model/types'

import { useEffect } from 'react'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import { Input } from '@/shared/components/ui/input'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { FieldType, ValueType } from '@/shared/generated'
import { numericOnly } from '@/shared/tools/constants'

export const getFieldEditor = (
	{
		field,
		value,
		options,
		onChange,
		invalid,
	}: {
		field: FieldOption
		value: string | boolean | undefined
		options: DataOption[]
		onChange: (value: string | boolean | undefined) => void
		invalid?: boolean
	},
	t: TFunction
) => {
	const type = field.schema.field_type
	switch (type) {
		case FieldType.Checkbox:
		case FieldType.Flag:
		case FieldType.Lock:
			return <CheckboxField value={value} onChange={onChange} />
		case FieldType.Badge:
		case FieldType.Chip:
		case FieldType.Choice:
			return (
				<Select
					aria-invalid={invalid}
					value={value as string}
					onValueChange={onChange}
				>
					<SelectTrigger
						className='w-full'
						data-testid='products-bulk-update-modal-field-value-select-trigger'
					>
						<SelectValue
							placeholder={t('general_select', { defaultValue: 'Select' })}
							data-testid='products-bulk-update-modal-field-value-select-value'
						/>
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{options.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
			)
		default:
			return (
				<Input
					data-testid='products-bulk-update-modal-field-value-input'
					aria-invalid={invalid}
					value={String(value)}
					placeholder={t('bulk_modal_enter_value', {
						defaultValue: 'Enter value or leave empty to clear the field',
					})}
					onChange={(event) =>
						onChange(
							[ValueType.Integer, ValueType.Number].includes(
								field.schema.value_type || ValueType.String
							)
								? event.target.value.replace(numericOnly, '')
								: event.target.value
						)
					}
				/>
			)
	}
}

const CheckboxField = ({
	value,
	onChange,
}: {
	value: any
	onChange: (value: boolean) => void
}) => {
	useEffect(() => {
		onChange(false)
	}, [])

	return (
		<div className='flex h-8 items-center'>
			<Checkbox
				data-testid='products-bulk-update-modal-field-value-checkbox'
				checked={Boolean(value)}
				onChange={(event) => onChange(event.target.checked)}
			/>
		</div>
	)
}
